import { PrecisionManufacturing } from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import React, { useState } from "react";

function isValidIPv4(str: string) {
  return /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d\d?)$/g.test(
    str
  );
}

type RobbyProps = {
  handleBack: () => void;
  handleNext: (robby: any) => void;
  handleSkip?: () => void;
};

const Robby: React.FC<RobbyProps> = ({
  handleBack,
  handleNext,
  handleSkip,
}) => {
  const { t } = useTranslation();

  const [configuration, setConfiguration] = useState<any>({
    FAST_STORE_PERIOD: 10,
    ROBIT: {
      type: 0,
      dhcp: true,
      ip: "0.0.0.0",
      mask: "0.0.0.0",
      gateway: "0.0.0.0",
      id: 120,
      baud: 500,
      dns: "0.0.0.0",
    },
  });

  return (
    <Grid
      container
      spacing={2}
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      <Grid item>
        <PrecisionManufacturing style={{ fontSize: "64px" }} />
      </Grid>
      <Grid item>
        <Typography variant="h5">{t("robby")}</Typography>
      </Grid>
      <Grid item>
        <Typography>{t("robbyDescription")}</Typography>
      </Grid>
      <Grid
        container
        item
        spacing={4}
        alignItems="center"
        justifyContent="center"
      >
        <Grid item xs={12} md={8} xl={6}>
          <Card variant="outlined">
            <CardContent style={{ margin: "8px" }}>
              <Grid container spacing={1} direction="column">
                <Grid item>
                  <TextField
                    onChange={(e) =>
                      setConfiguration({
                        ...configuration,
                        ROBIT: {
                          ...configuration.ROBIT,
                          id: Number(e.target.value),
                        },
                      })
                    }
                    value={configuration.ROBIT.id}
                    fullWidth
                    size="small"
                    type="number"
                    label={t("id")}
                  />
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(e) =>
                      setConfiguration({
                        ...configuration,
                        ROBIT: {
                          ...configuration.ROBIT,
                          baud: Number(e.target.value),
                        },
                      })
                    }
                    value={configuration.ROBIT.baud}
                    fullWidth
                    size="small"
                    label={t("baudrate")}
                  />
                </Grid>
                <Grid item>
                  <Typography variant="h6">{t("ipv4")}</Typography>
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(e) =>
                      setConfiguration({
                        ...configuration,
                        ROBIT: {
                          ...configuration.ROBIT,
                          ip: e.target.value,
                        },
                      })
                    }
                    value={configuration.ROBIT.ip}
                    fullWidth
                    size="small"
                    label={t("ipAddress")}
                    error={
                      !!configuration.ROBIT.ip &&
                      !isValidIPv4(configuration.ROBIT.ip)
                    }
                    helperText={
                      !!configuration.ROBIT.ip &&
                      !isValidIPv4(configuration.ROBIT.ip)
                        ? t("invalidIpv4Address")
                        : undefined
                    }
                  />
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(e) =>
                      setConfiguration({
                        ...configuration,
                        ROBIT: {
                          ...configuration.ROBIT,
                          mask: e.target.value,
                        },
                      })
                    }
                    value={configuration.ROBIT.mask}
                    fullWidth
                    size="small"
                    label={t("subnetMask")}
                    error={
                      !!configuration.ROBIT.mask &&
                      !isValidIPv4(configuration.ROBIT.mask)
                    }
                    helperText={
                      !!configuration.ROBIT.mask &&
                      !isValidIPv4(configuration.ROBIT.mask)
                        ? t("invalidIpv4Address")
                        : undefined
                    }
                  />
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(e) =>
                      setConfiguration({
                        ...configuration,
                        ROBIT: {
                          ...configuration.ROBIT,
                          gateway: e.target.value,
                        },
                      })
                    }
                    value={configuration.ROBIT.gateway}
                    fullWidth
                    size="small"
                    label={t("gateway")}
                    error={
                      !!configuration.ROBIT.gateway &&
                      !isValidIPv4(configuration.ROBIT.gateway)
                    }
                    helperText={
                      !!configuration.ROBIT.gateway &&
                      !isValidIPv4(configuration.ROBIT.gateway)
                        ? t("invalidIpv4Address")
                        : undefined
                    }
                  />
                </Grid>
                <Grid item>
                  <TextField
                    onChange={(e) =>
                      setConfiguration({
                        ...configuration,
                        ROBIT: {
                          ...configuration.ROBIT,
                          dns: e.target.value,
                        },
                      })
                    }
                    value={configuration.ROBIT.dns}
                    fullWidth
                    size="small"
                    label={t("dns")}
                    error={
                      !!configuration.ROBIT.dns &&
                      !isValidIPv4(configuration.ROBIT.dns)
                    }
                    helperText={
                      !!configuration.ROBIT.dns &&
                      !isValidIPv4(configuration.ROBIT.dns)
                        ? t("invalidIpv4Address")
                        : undefined
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Grid
            container
            spacing={2}
            display="flex"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <Grid item>
              <Button
                size="large"
                variant="outlined"
                style={{ float: "right", marginTop: "8px" }}
                onClick={handleBack}
              >
                {t("back")}
              </Button>
            </Grid>
            <Tooltip title={""}>
              <Grid item>
                <Button
                  size="large"
                  variant="contained"
                  style={{ float: "right", marginTop: "8px" }}
                  onClick={() => handleNext(configuration)}
                  disabled={
                    !configuration.ROBIT.ip ||
                    !configuration.ROBIT.dns ||
                    !configuration.ROBIT.gateway ||
                    !configuration.ROBIT.mask
                  }
                >
                  {t("next")}
                </Button>
                {handleSkip && (
                  <Button
                    size="large"
                    style={{
                      float: "right",
                      marginTop: "8px",
                      marginRight: "8px",
                    }}
                    onClick={handleSkip}
                  >
                    {t("skip")}
                  </Button>
                )}
              </Grid>
            </Tooltip>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default Robby;
